import { Injectable, signal, computed, inject, DestroyRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { User, LoginRequest, RegisterRequest, AuthResponse } from '../models/user.model';
import { UserService } from './user.service';
import { AwsService } from './aws.service';
import { environment } from '../../environments/environment';
import { ROLES } from '../constants/roles';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly API_URL = environment.apiUrl;
  private readonly destroyRef = inject(DestroyRef);

  private userSignal = signal<User | null>(null);
  private tokenSignal = signal<string | null>(null);

  readonly user = this.userSignal.asReadonly();
  readonly token = this.tokenSignal.asReadonly();
  readonly isAuthenticated = computed(() => !!this.tokenSignal());
  readonly isAdmin = computed(() => this.userSignal()?.role === ROLES.ADMIN);
  readonly isStudent = computed(() => this.userSignal()?.role === ROLES.STUDENT);

  constructor(
    private http: HttpClient,
    private router: Router,
    private userService: UserService,
    private awsService: AwsService
  ) {
    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    const token = localStorage.getItem('token');
    const accessToken = localStorage.getItem('accessToken');
    const idToken = localStorage.getItem('idToken');
    const user = localStorage.getItem('user');

    if (accessToken || idToken) {
      this.tokenSignal.set(idToken || accessToken);
      if (user) {
        try {
          this.userSignal.set(JSON.parse(user));
        } catch {
          this.fetchUserFromApi();
        }
      } else {
        this.fetchUserFromApi();
      }
    } else if (token && user) {
      this.tokenSignal.set(token);
      try {
        this.userSignal.set(JSON.parse(user));
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
  }

  private fetchUserFromApi(): void {
    this.userService.getMe().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (user) => {
        this.userSignal.set(user);
        localStorage.setItem('user', JSON.stringify(user));
      },
      error: () => {
        this.tokenSignal.set(null);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('idToken');
        localStorage.removeItem('token');
      }
    });
  }

  private pendingUsername: string | null = null;

  login(credentials: LoginRequest): Observable<User> {
    return new Observable(observer => {
      this.awsService.authenticate(credentials.email, credentials.password).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe({
        next: () => {
          this.userService.getMe().pipe(
            takeUntilDestroyed(this.destroyRef)
          ).subscribe({
            next: (userData: User) => {
              const user: User = {
                id: userData.id || userData.email,
                email: userData.email,
                firstName: userData.firstName,
                lastName: userData.lastName,
                avatar: userData.avatar,
                role: userData.role,
                status: userData.status || 'enabled',
                createdAt: userData.createdAt || new Date()
              };

              this.tokenSignal.set(localStorage.getItem('idToken') || localStorage.getItem('accessToken'));
              this.userSignal.set(user);
              localStorage.setItem('user', JSON.stringify(user));

              observer.next(user);
              observer.complete();
            },
            error: (err: unknown) => {
              observer.error(err);
            }
          });
        },
        error: (error: unknown) => {
          if (error && typeof error === 'object' && 'reason' in error && error.reason === 'FORCE_CHANGE_PASSWORD') {
            this.pendingUsername = credentials.email;
            observer.error({ ...error as object, challengeName: 'NEW_PASSWORD_REQUIRED' });
          } else {
            observer.error(error);
          }
        }
      });
    });
  }

  completeNewPassword(newPassword: string): Observable<User> {
    return new Observable(observer => {
      this.awsService.completeNewPasswordChallenge(newPassword).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe({
        next: (tokens: { accessToken: string; idToken: string }) => {
          localStorage.setItem('accessToken', tokens.accessToken);
          localStorage.setItem('idToken', tokens.idToken);
          this.tokenSignal.set(tokens.idToken || tokens.accessToken);
          this.pendingUsername = null;

          this.userService.getMe().pipe(
            takeUntilDestroyed(this.destroyRef)
          ).subscribe({
            next: (userData: User) => {
              const user: User = {
                id: userData.id || userData.email,
                email: userData.email,
                firstName: userData.firstName,
                lastName: userData.lastName,
                avatar: userData.avatar,
                role: userData.role,
                status: userData.status || 'enabled',
                createdAt: userData.createdAt || new Date()
              };

              this.userSignal.set(user);
              localStorage.setItem('user', JSON.stringify(user));

              observer.next(user);
              observer.complete();
            },
            error: (err: unknown) => {
              observer.error(err);
            }
          });
        },
        error: (error: unknown) => {
          observer.error(error);
        }
      });
    });
  }

  getPendingUsername(): string | null {
    return this.pendingUsername;
  }

  register(data: RegisterRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.API_URL}/auth/register`, data).pipe(
      tap(response => this.handleAuth(response))
    );
  }

  forgotPassword(email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.API_URL}/auth/forgot-password`, { email });
  }

  resetPassword(data: { email: string; password: string; code: string }): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.API_URL}/auth/reset-password`, data);
  }

  logout(): void {
    this.tokenSignal.set(null);
    this.userSignal.set(null);
    localStorage.clear();
    this.userService.logout();
    this.awsService.logout().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({ error: () => {} });
    this.router.navigate(['/']);
  }

  setToken(idToken: string): void {
    this.tokenSignal.set(idToken);
  }

  updateProfile(data: Partial<User> & { mimeType?: string }): Observable<User> {
    return this.http.put<User>(`${this.API_URL}/user/me`, data).pipe(
      tap(user => {
        this.userSignal.set(user);
        localStorage.setItem('user', JSON.stringify(user));
      })
    );
  }

  updateAvatar(avatar: string, mimeType?: string): Observable<User> {
    const payload: any = { requestPictureUrl: Boolean(mimeType) };
    if (mimeType) payload.mimeType = mimeType;
    if (avatar) payload.avatar = avatar;
    return this.http.put<User>(`${this.API_URL}/user/me`, payload).pipe(
      tap(user => {
        const currentUser = this.userSignal();
        const newAvatar = user.avatar || currentUser?.avatar;
        const updatedUser = { ...user, avatar: newAvatar };
        this.userSignal.set(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      })
    );
  }

  getCurrentUser(): User | null {
    return this.userSignal();
  }

  updateUserAvatar(avatarUrl: string | null): void {
    if (!avatarUrl) return;
    const currentUser = this.userSignal();
    if (currentUser) {
      const updatedUser = { ...currentUser, avatar: avatarUrl };
      this.userSignal.set(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  }

  private handleAuth(response: AuthResponse): void {
    this.tokenSignal.set(response.token);
    this.userSignal.set(response.user);
    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(response.user));
  }
}